import { apiCall } from '~/providers';
import {
  GetOverviewSummary, GetOverviewThermalComfort, GetOverviewUnitHealth, GetUnitHealthHistory, GetThermalComfortHistory, GetEnergyTrendsHistory, OverviewInfo,
} from '../types';
import { WaterOverviewInfo } from 'providers/types/api-private';
import { verifyArrayParameters } from 'helpers/genericHelper';
import { HealthStatusOverviewFilter, ThermalComfortStatusFilter } from '~/providers/types/overview';

type FilterOptions = {
  stateIds?: string[];
  unitIds?: number[];
  healthStatus?: string;
  thermalComfortStatus?: string;
}

export async function getOverviewInfo(
  clientId: number,
): Promise<OverviewInfo | undefined> {
  try {
    const response = await apiCall('/clients/get-overview-info', {
      CLIENT_ID: clientId,
    });

    return {
      hasWater: response.HAS_WATER_DEVICES,
      hasHealth: response.HAS_HEALTH_CONTROL,
      hasEnergy: response.HAS_ENERGY_DEVICES,
      hasThermalComfort: response.HAS_THERMAL_COMFORT_CONTROL,
    };
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getWaterOverviewInfo(
  clientId: number,
  filterOptions?: FilterOptions,
): Promise<WaterOverviewInfo | undefined> {
  try {
    const response = await apiCall('/water/get-overview-info', {
      CLIENT_ID: clientId,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getOverviewSummary(
  clientId: number,
  filterOptions?: FilterOptions,
): Promise<GetOverviewSummary | undefined> {
  try {
    const response = await apiCall('/clients/get-overview-summary', {
      CLIENT_ID: clientId,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getOverviewUnitHealth(
  clientId: number,
  filterOptions?: FilterOptions,
): Promise<GetOverviewUnitHealth | undefined> {
  try {
    const response = await apiCall('/unit-health/get-overview-info', {
      CLIENT_ID: clientId,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getOverviewThermalComfort(
  clientId: number,
  filterOptions?: FilterOptions,
): Promise<GetOverviewThermalComfort | undefined> {
  try {
    const response = await apiCall('/thermal-comfort/get-overview-info', {
      CLIENT_ID: clientId,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getUnitHealthHistory(
  clientId: number,
  filterOptions?: FilterOptions,
): Promise<GetUnitHealthHistory | undefined> {
  try {
    const response = await apiCall('/unit-health/get-history', {
      CLIENT_ID: clientId,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function getThermalComfortHistory(
  clientId: number,
  filterOptions?: FilterOptions,
): Promise<GetThermalComfortHistory | undefined> {
  try {
    const response = await apiCall('/thermal-comfort/get-history', {
      CLIENT_ID: clientId,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}
export async function getEnergyTrendsHistory(
  clientId: number,
  filterOptions?: FilterOptions,
): Promise<GetEnergyTrendsHistory | undefined> {
  try {
    const response = await apiCall('/energy/get-trends-history', {
      CLIENT_ID: clientId,
      STATE_IDS: verifyArrayParameters(filterOptions?.stateIds),
      UNIT_IDS: verifyArrayParameters(filterOptions?.unitIds),
      HEALTH_STATUS: verifyArrayParameters(parseHealthStatus(filterOptions?.healthStatus)),
      THERMAL_COMFORT_STATUS: parseThermalComfortStatus(filterOptions?.thermalComfortStatus),
    });

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

function parseHealthStatus(healthStatus?: string): HealthStatusOverviewFilter[] | undefined {
  if (!healthStatus) return undefined;

  return [healthStatus as HealthStatusOverviewFilter];
}

function parseThermalComfortStatus(thermalComfortStatus?: string): ThermalComfortStatusFilter | undefined {
  if (!thermalComfortStatus) return undefined;

  return thermalComfortStatus as ThermalComfortStatusFilter;
}
